import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { Button, ButtonProps, Menu, MenuProps, PaperProps, Tooltip } from '@mui/material';
import clsx from 'clsx';
import { MouseEvent, useEffect, useRef, useState } from 'react';

interface Props extends Omit<MenuProps, 'anchorEl' | 'onClose' | 'open'> {
    anchorEl?: MenuProps['anchorEl'];
    onClose?: MenuProps['onClose'];
    open?: boolean;
    closeWhen?: boolean;
    buttonProps?: ButtonProps & { tooltip?: string };
}

const Dropdown = ({ anchorEl, open, onClose, closeWhen, buttonProps, ...props }: Props) => {
    const [internalAnchorEl, setInternalAnchorEl] = useState<HTMLElement | null>(null);
    const [buttonWidth, setButtonWidth] = useState<number | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const isControlled = anchorEl !== undefined;
    const resolvedAnchorEl = isControlled ? anchorEl : internalAnchorEl;
    const resolvedOpen = isControlled ? open ?? Boolean(resolvedAnchorEl) : Boolean(resolvedAnchorEl);
    const anchorWidth =
        typeof HTMLElement !== 'undefined' && resolvedAnchorEl instanceof HTMLElement
            ? resolvedAnchorEl.getBoundingClientRect().width
            : buttonWidth ?? undefined;
    const { tooltip, onClick: onButtonClick, ...restButtonProps } = buttonProps ?? {};
    const externalPaperProps = props.slotProps?.paper as PaperProps | undefined;

    useEffect(() => {
        const button = buttonRef.current;
        if (!button) return;

        const update = () => setButtonWidth(button.getBoundingClientRect().width);
        const observer = new ResizeObserver(update);
        observer.observe(button);
        update();
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isControlled && closeWhen) {
            setInternalAnchorEl(null);
        }
    }, [closeWhen, isControlled]);

    const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (!isControlled) {
            setInternalAnchorEl(event.currentTarget);
        }
        onButtonClick?.(event);
    };

    const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (!isControlled) {
            setInternalAnchorEl(null);
        }
        onClose?.(event, reason);
    };

    return (
        <>
            <Tooltip title={tooltip ?? ''} arrow placement="right">
                <span className="w-full">
                    <Button
                        size="large"
                        {...restButtonProps}
                        onClick={handleButtonClick}
                        ref={buttonRef}
                        className={clsx(
                            'font-roboto justify-start px-4',
                            {
                                'rounded-none rounded-t-lg': Boolean(resolvedAnchorEl),
                            },
                            restButtonProps.className
                        )}
                    >
                        {restButtonProps.children}
                        <ExpandMoreOutlinedIcon className="ml-auto" />
                    </Button>
                </span>
            </Tooltip>
            <Menu
                sx={{
                    boxShadow: 'none',
                    '& .MuiPaper-root': {
                        width: anchorWidth,
                    },
                }}
                {...props}
                anchorEl={resolvedAnchorEl}
                open={resolvedOpen}
                onClose={handleClose}
                classes={{
                    ...props.classes,
                    paper: clsx('rounded-none rounded-b-lg', props.classes?.paper),
                    list: clsx('py-0', props.classes?.list),
                }}
                slotProps={{
                    ...props.slotProps,
                    paper: {
                        ...externalPaperProps,
                        sx: { width: anchorWidth, ...externalPaperProps?.sx },
                    },
                }}
            />
        </>
    );
};

export { Dropdown };
